import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Search, Download, Eye, Calendar } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { format, subDays } from 'date-fns';

interface FailedForward {
  id: string;
  event_id: string;
  success: boolean;
  duration_ms: number | null;
  error_message: string | null;
  created_at: string;
  signal_event: {
    action: string;
    instrument: string;
    bot_id: string;
    signal_token: string;
    bot_name: string;
  } | null;
  endpoint: {
    name: string;
    parent_bot_name: string;
  } | null;
}

interface ErrorCategory {
  category: string;
  count: number;
  percentage: number;
  sample_error: string;
}

export const BotMergeErrorDebug: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [timeframe, setTimeframe] = useState<'today' | '7d' | '30d'>('7d');
  const [selectedError, setSelectedError] = useState<FailedForward | null>(null);

  const getDateRange = () => {
    const now = new Date();
    switch (timeframe) {
      case 'today':
        return { start: new Date(now.setHours(0, 0, 0, 0)), end: new Date() };
      case '7d':
        return { start: subDays(new Date(), 7), end: new Date() };
      case '30d':
        return { start: subDays(new Date(), 30), end: new Date() };
      default:
        return { start: subDays(new Date(), 7), end: new Date() };
    }
  };

  const { start, end } = getDateRange();

  // Failed forwards data
  const { data: failedForwards = [], isLoading: forwardsLoading } = useQuery({
    queryKey: ['bot-merge-failed-forwards', timeframe, searchTerm],
    queryFn: async (): Promise<FailedForward[]> => {
      // First get the failed logs
      const { data: logs, error: logsError } = await supabase
        .from('bot_signal_forward_logs')
        .select(`
          id,
          event_id,
          success,
          duration_ms,
          error_message,
          created_at,
          endpoint_id
        `)
        .eq('success', false)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())
        .order('created_at', { ascending: false })
        .limit(100);

      if (logsError) throw logsError;

      const results: FailedForward[] = [];

      // For each log, get the related signal event and endpoint info
      for (const log of logs || []) {
        // Get signal event info
        const { data: signalEvent } = await supabase
          .from('bot_signal_events')
          .select(`
            action,
            instrument,
            bot_id,
            signal_token,
            bots:bot_id (name)
          `)
          .eq('id', log.event_id)
          .single();

        // Get endpoint info
        const { data: endpoint } = await supabase
          .from('bot_signal_forward_endpoints')
          .select(`
            name,
            bots:parent_bot_id (name)
          `)
          .eq('id', log.endpoint_id)
          .single();

        const processedLog: FailedForward = {
          ...log,
          signal_event: signalEvent ? {
            action: signalEvent.action,
            instrument: signalEvent.instrument,
            bot_id: signalEvent.bot_id,
            signal_token: signalEvent.signal_token,
            bot_name: signalEvent.bots?.[0]?.name || 'Unknown'
          } : null,
          endpoint: endpoint ? {
            name: endpoint.name,
            parent_bot_name: endpoint.bots?.[0]?.name || 'Unknown'
          } : null
        };

        // Apply search filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          if (
            log.error_message?.toLowerCase().includes(searchLower) ||
            processedLog.signal_event?.bot_name?.toLowerCase().includes(searchLower) ||
            processedLog.signal_event?.instrument?.toLowerCase().includes(searchLower) ||
            processedLog.endpoint?.parent_bot_name?.toLowerCase().includes(searchLower)
          ) {
            results.push(processedLog);
          }
        } else {
          results.push(processedLog);
        }
      }

      return results;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Error categorization
  const { data: errorCategories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['bot-merge-error-categories', timeframe],
    queryFn: async (): Promise<ErrorCategory[]> => {
      const { data, error } = await supabase
        .from('bot_signal_forward_logs')
        .select('error_message')
        .eq('success', false)
        .not('error_message', 'is', null)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (error) throw error;

      const errorMessages = data?.map(item => item.error_message!) || [];
      const total = errorMessages.length;

      if (total === 0) return [];

      // Categorize errors
      const categories: { [key: string]: { count: number; sample: string } } = {};

      errorMessages.forEach(message => {
        let category = 'Other';
        
        if (message.toLowerCase().includes('timeout') || message.toLowerCase().includes('timed out')) {
          category = 'Timeout';
        } else if (message.toLowerCase().includes('connection') || message.toLowerCase().includes('connect')) {
          category = 'Connection Error';
        } else if (message.toLowerCase().includes('404') || message.toLowerCase().includes('not found')) {
          category = 'Not Found (404)';
        } else if (message.toLowerCase().includes('500') || message.toLowerCase().includes('internal server')) {
          category = 'Server Error (500)';
        } else if (message.toLowerCase().includes('401') || message.toLowerCase().includes('unauthorized')) {
          category = 'Authentication Error';
        } else if (message.toLowerCase().includes('400') || message.toLowerCase().includes('bad request')) {
          category = 'Bad Request (400)';
        } else if (message.toLowerCase().includes('network') || message.toLowerCase().includes('fetch')) {
          category = 'Network Error';
        }

        if (!categories[category]) {
          categories[category] = { count: 0, sample: message };
        }
        categories[category].count++;
      });

      return Object.entries(categories)
        .map(([category, data]) => ({
          category,
          count: data.count,
          percentage: Math.round((data.count / total) * 100 * 100) / 100,
          sample_error: data.sample,
        }))
        .sort((a, b) => b.count - a.count);
    },
    refetchInterval: 60000,
  });

  const exportLogs = () => {
    const csvContent = failedForwards.map(log => ({
      timestamp: log.created_at,
      bot_name: log.signal_event?.bot_name || 'Unknown',
      parent_bot: log.endpoint?.parent_bot_name || 'Unknown',
      action: log.signal_event?.action || '',
      instrument: log.signal_event?.instrument || '',
      endpoint_name: log.endpoint?.name || '',
      error_message: log.error_message || '',
      response_time: log.duration_ms || 0,
    }));

    const csv = [
      Object.keys(csvContent[0] || {}).join(','),
      ...csvContent.map(row => Object.values(row).map(val => `"${val}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bot-merge-error-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Error Debug Center</h2>
          <p className="text-muted-foreground">Analyze and troubleshoot failed signal forwards</p>
        </div>
        <Button onClick={exportLogs} disabled={failedForwards.length === 0} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export Logs
        </Button>
      </div>

      {/* Time Filter */}
      <Tabs value={timeframe} onValueChange={(value) => setTimeframe(value as any)}>
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="7d">Last 7 Days</TabsTrigger>
          <TabsTrigger value="30d">Last 30 Days</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by error message, bot name, or instrument..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Error Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Error Categories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {categoriesLoading ? (
              <div className="text-center py-4">Loading categories...</div>
            ) : errorCategories.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">No error data available</div>
            ) : (
              errorCategories.map((category) => (
                <div key={category.category} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="destructive">{category.category}</Badge>
                    <div>
                      <div className="font-medium">{category.count} errors</div>
                      <div className="text-sm text-muted-foreground truncate max-w-md">
                        {category.sample_error}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{category.percentage}%</div>
                    <div className="text-sm text-muted-foreground">of total errors</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Failed Forwards Table */}
      <Card>
        <CardHeader>
          <CardTitle>Failed Forwards ({failedForwards.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {forwardsLoading ? (
              <div className="text-center py-8">Loading failed forwards...</div>
            ) : failedForwards.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No failed forwards found for the selected timeframe
              </div>
            ) : (
              failedForwards.map((forward) => (
                <div key={forward.id} className="border rounded-lg p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="destructive">Failed</Badge>
                      <span className="font-medium">
                        {forward.signal_event?.bot_name || 'Unknown Bot'}
                      </span>
                      <Badge variant="outline">
                        {forward.signal_event?.action} {forward.signal_event?.instrument}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(forward.created_at), 'MM/dd HH:mm:ss')}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedError(selectedError?.id === forward.id ? null : forward)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Target */}
                  <div className="text-sm text-muted-foreground">
                    <strong>Target:</strong> {forward.endpoint?.parent_bot_name || 'Unknown Parent'}
                    {forward.endpoint?.name && ` (${forward.endpoint.name})`}
                  </div>

                  {/* Error Message */}
                  <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                    <div className="text-sm font-medium text-red-500 mb-1">Error Message:</div>
                    <div className="text-sm text-red-600">
                      {forward.error_message || 'No error message available'}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedError?.id === forward.id && (
                    <div className="border-t pt-3 space-y-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <strong>Signal Event ID:</strong> {forward.event_id}
                        </div>
                        <div>
                          <strong>Endpoint:</strong> {forward.endpoint?.name || 'Unknown'}
                        </div>
                        <div>
                          <strong>Signal Token:</strong> {forward.signal_event?.signal_token || 'N/A'}
                        </div>
                        <div>
                          <strong>Response Time:</strong> {forward.duration_ms || 'N/A'}ms
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};